/*
 * currency-convert.js — portable display-only currency converter for Shopify
 * ---------------------------------------------------------------------------
 * Shows storefront prices in the visitor's local currency based on their IP,
 * for stores that CANNOT use Shopify Markets multi-currency (e.g. India-based
 * stores without Shopify Payments). Rates come from frankfurter.dev; the
 * visitor's country comes from Shopify's native browsing_context_suggestions
 * endpoint — no third-party geo API.
 *
 * IMPORTANT: this is display-only. Shopify checkout still charges the store's
 * base currency. A notice is shown to the customer to make this clear.
 * The chosen currency + rate are handed to checkout as cart attributes so the
 * checkout UI extension can show matching approximate amounts there too.
 *
 * Config is provided by snippets/currency-convert.liquid as window.CC_CONFIG.
 * The country -> currency rules live in CURRENCY_BY_COUNTRY below and are the
 * one place to edit for different stores.
 */
(function () {
  'use strict';

  var C = window.CC_CONFIG || {};
  if (C.enabled === false) return;
  if (window.Shopify && Shopify.designMode) return; // don't run in the editor

  var BASE = (C.base || 'INR').toUpperCase();
  var API = C.apiBase || 'https://api.frankfurter.dev/v1';
  var AED_PER_USD = C.aedPerUsd || 3.6725; // AED is hard-pegged to USD
  var DECIMALS = C.decimals; // null => each currency's own convention
  var DEBUG = !!C.debug;
  var SHOW_SELECTOR = C.showSelector !== false;

  /* Country -> display currency.
     Ordered by the store's real session numbers. Europe is deliberately shown
     EUR rather than each national currency (SEK, CHF, PLN, DKK, NOK...) — a
     single European price is easier to reason about than fifteen. Everywhere
     else gets its own currency where a rate exists; unmapped countries fall
     back to USD via FALLBACK below. */
  var CURRENCY_BY_COUNTRY = C.map || {
    // --- top non-European traffic, each in its own currency ---
    US: 'USD',                      // United States
    AE: 'AED',                      // United Arab Emirates
    PK: 'PKR',                      // Pakistan
    CA: 'CAD',                      // Canada
    GB: 'GBP',                      // United Kingdom — local currency, not EUR
    EG: 'EGP',                      // Egypt
    AU: 'AUD',                      // Australia
    SG: 'SGD',                      // Singapore
    NP: 'NPR',                      // Nepal (pegged to INR at 1.60)
    KW: 'KWD',                      // Kuwait
    JP: 'JPY',                      // Japan
    VN: 'VND',                      // Vietnam

    // --- neighbours of the above, likely next in the list ---
    NZ: 'NZD', MX: 'MXN', SA: 'SAR', QA: 'QAR', OM: 'OMR', BH: 'BHD',
    LK: 'LKR', BD: 'BDT', MY: 'MYR', TH: 'THB', PH: 'PHP', ID: 'IDR',
    HK: 'HKD', CN: 'CNY', KR: 'KRW', TW: 'TWD', ZA: 'ZAR', BR: 'BRL',
    TR: 'TRY', IL: 'ILS',

    // --- Europe: one currency for the whole region, by request ---
    AD: 'EUR', AT: 'EUR', BE: 'EUR', BG: 'EUR', HR: 'EUR', CY: 'EUR', CZ: 'EUR',
    DK: 'EUR', EE: 'EUR', FI: 'EUR', FR: 'EUR', DE: 'EUR', GR: 'EUR', HU: 'EUR',
    IS: 'EUR', IE: 'EUR', IT: 'EUR', LV: 'EUR', LI: 'EUR', LT: 'EUR', LU: 'EUR',
    MT: 'EUR', MC: 'EUR', NL: 'EUR', NO: 'EUR', PL: 'EUR', PT: 'EUR', RO: 'EUR',
    SK: 'EUR', SI: 'EUR', ES: 'EUR', SE: 'EUR', CH: 'EUR', SM: 'EUR', VA: 'EUR', GI: 'EUR'
  };

  /* Anywhere we can identify that isn't listed above and isn't a base-currency
     country falls back to USD — Japan, Brazil, Hong Kong and the rest read a
     dollar price far more easily than a rupee one. Countries listed in
     baseCountries keep the store's own currency and are never converted. */
  var FALLBACK = (C.fallbackCurrency || 'USD').toUpperCase();
  var BASE_COUNTRIES = (C.baseCountries || ['IN']).map(function (c) { return c.toUpperCase(); });

  var LS_CUR = 'cc_currency_v1';   // manual override
  var LS_RATES = 'cc_rates_v1';    // cached rates
  var LS_GEO = 'cc_geo_v1';        // cached detected country (fallback path only)
  var LS_COUNTRY = 'cc_country_v1'; // last market country we saw, to spot a switch

  function log() { if (DEBUG && window.console) console.log.apply(console, ['[currency]'].concat([].slice.call(arguments))); }

  // ---------- currency selection ----------
  function mapCountry(cc) {
    // No country at all (geo blocked or failed) stays on the base currency:
    // most of this store's traffic is domestic, so guessing USD would be worse
    // than leaving prices alone.
    if (!cc) return BASE;
    cc = cc.toUpperCase();
    if (CURRENCY_BY_COUNTRY[cc]) return CURRENCY_BY_COUNTRY[cc].toUpperCase();
    if (BASE_COUNTRIES.indexOf(cc) !== -1) return BASE;
    return FALLBACK;
  }
  function getManual() { try { return localStorage.getItem(LS_CUR); } catch (e) { return null; } }

  function cachedGeo() { try { var r = JSON.parse(localStorage.getItem(LS_GEO)); if (r && r.cc && (Date.now() - r.ts) < 30 * 864e5) return r.cc; } catch (e) {} return null; }
  function cacheGeo(cc) { try { localStorage.setItem(LS_GEO, JSON.stringify({ cc: cc, ts: Date.now() })); } catch (e) {} }

  function detectCountry() {
    return new Promise(function (resolve) {
      var cc = cachedGeo();
      if (cc) { resolve(cc); return; }
      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      var current = (window.Shopify && Shopify.country) || '';
      fetch(root + 'browsing_context_suggestions.json?country[enabled]=true&country[exclude]=' + encodeURIComponent(current), { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) {
          log('geo response', d);
          var c = (d && ((d.detected_values && d.detected_values.country && d.detected_values.country.handle) ||
            (d.suggestions && d.suggestions[0] && d.suggestions[0].country && d.suggestions[0].country.handle))) || current;
          c = (c || '').toUpperCase();
          if (c) cacheGeo(c);
          resolve(c);
        })
        .catch(function () { resolve((current || '').toUpperCase()); });
    });
  }

  // ---------- rates ----------
  function cachedRates() { try { var r = JSON.parse(localStorage.getItem(LS_RATES)); if (r && r.base === BASE && r.rates && (Date.now() - r.ts) < 12 * 36e5) return r.rates; } catch (e) {} return null; }
  function cacheRates(rates) { try { localStorage.setItem(LS_RATES, JSON.stringify({ base: BASE, rates: rates, ts: Date.now() })); } catch (e) {} }

  /* Rates. The ECB feed frankfurter serves covers only ~30 currencies and has
     nothing for PKR, EGP, NPR, KWD or VND — between them more traffic than
     Germany, France and the Netherlands combined. open.er-api.com carries 166
     and needs no key, so it leads; frankfurter stays as the fallback for the
     majors if it is ever unreachable. */
  function normalise(rates) {
    var out = {};
    Object.keys(rates || {}).forEach(function (k) { out[k.toUpperCase()] = rates[k]; });
    if (!out.AED && out.USD) out.AED = out.USD * AED_PER_USD; // hard peg, if the feed omits it
    out[BASE] = 1;
    return out;
  }

  function fetchJson(url) {
    return fetch(url).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
  }

  function getRates() {
    var cached = cachedRates();
    if (cached) return Promise.resolve(cached);

    var primary = (C.ratesApi || 'https://open.er-api.com/v6/latest/') + encodeURIComponent(BASE);
    return fetchJson(primary)
      .then(function (d) {
        if (d && d.result === 'success' && d.rates) return normalise(d.rates);
        log('primary rate source unavailable, falling back to frankfurter');
        return fetchJson(API + '/latest?base=' + encodeURIComponent(BASE))
          .then(function (f) { return f && f.rates ? normalise(f.rates) : {}; });
      })
      .then(function (rates) {
        if (Object.keys(rates).length > 1) cacheRates(rates);
        return rates;
      });
  }

  // ---------- formatting ----------
  /* With no decimals configured, let Intl use each currency's own convention:
     ¥2,050 and ₫412,000 carry no minor unit, while €13.66 does. */
  function fmt(value, cur) {
    var opts = { style: 'currency', currency: cur };
    if (DECIMALS != null) { opts.minimumFractionDigits = DECIMALS; opts.maximumFractionDigits = DECIMALS; }
    try { return new Intl.NumberFormat(undefined, opts).format(value); }
    catch (e) { return cur + ' ' + value.toFixed(DECIMALS == null ? 2 : DECIMALS); }
  }

  // ---------- DOM conversion ----------
  function esc(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
  var SYMS = C.baseSymbols || ['Rs.', 'Rs', '₹', 'INR'];
  var symPattern = SYMS.map(esc).join('|');
  // A money token: <symbol> 1,234.56  (optionally followed by a trailing base code, e.g. " INR")
  var reReplace = new RegExp('(?:' + symPattern + ')\\s*([0-9][0-9,]*(?:\\.[0-9]{1,2})?)(?:\\s*' + esc(BASE) + ')?', 'gi');
  var reTest = new RegExp('(?:' + symPattern + ')\\s*[0-9]', 'i');

  var ROOTS = C.selectors || [
    '.price', '.cart-item__price-wrapper', '.cart-item__totals', '.cart-item__discounted-prices',
    '.cart-item__final-price', '.cart-item__old-price', '.totals', '.totals__total-value',
    '.cart-reco__now', '.cart-reco__was', '.unit-price', '.cart-shipping-bar', '[data-cc-money]',
    // Freeze Studios: the sticky add-to-cart bar carries its own price, the
    // free-shipping bar quotes an amount remaining, and installed apps render
    // their own money spans (e.g. .conversion-bear-money on cart lines) which
    // all carry the conventional .money class.
    '.ctm_satc__price', '.cart-drawer__free-shipping', '.money'
  ];

  var CUR = BASE, rate = 1, observer = null;

  function convertText(t) {
    return t.replace(reReplace, function (m, num) {
      var v = parseFloat(num.replace(/,/g, ''));
      if (isNaN(v)) return m;
      return fmt(v * rate, CUR);
    });
  }

  function walk(root) {
    if (!root || root.nodeType !== 1) return;
    var w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (n) {
        var p = n.parentNode;
        if (p && (p.nodeName === 'SCRIPT' || p.nodeName === 'STYLE' || p.nodeName === 'TEXTAREA')) return NodeFilter.FILTER_REJECT;
        return (n.nodeValue && reTest.test(n.nodeValue)) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    });
    var nodes = [], cur;
    while ((cur = w.nextNode())) nodes.push(cur);
    nodes.forEach(function (n) { var nv = convertText(n.nodeValue); if (nv !== n.nodeValue) n.nodeValue = nv; });
  }

  function convertRoots(scope) {
    var ctx = scope && scope.querySelectorAll ? scope : document;
    ROOTS.forEach(function (sel) {
      if (scope && scope.nodeType === 1 && scope.matches && scope.matches(sel)) walk(scope);
      ctx.querySelectorAll(sel).forEach(walk);
    });
  }

  function convertAll() {
    if (CUR === BASE) return;
    if (observer) observer.disconnect();
    convertRoots(document);
    if (observer) observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  function startObserver() {
    if (CUR === BASE) return;
    observer = new MutationObserver(function (muts) {
      observer.disconnect();
      muts.forEach(function (m) {
        if (m.type === 'childList') {
          m.addedNodes.forEach(function (node) {
            if (node.nodeType === 1) convertRoots(node);
            else if (node.nodeType === 3 && node.parentNode) walk(node.parentNode);
          });
        } else if (m.type === 'characterData' && m.target.parentNode && reTest.test(m.target.nodeValue || '')) {
          walk(m.target.parentNode);
        }
      });
      observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  // ---------- customer notice (see != pay) ----------
  function injectNotice() {
    if (CUR === BASE) return;
    var msg = (C.noticeText || 'Prices shown in {CUR} are indicative. Your order is charged in {BASE} at checkout.')
      .replace('{CUR}', CUR).replace('{BASE}', BASE);
    var spots = C.noticeSelectors || ['.product-form__buttons', '.cart-drawer__footer', '.cart__footer', '.cart__ctas'];
    for (var i = 0; i < spots.length; i++) {
      document.querySelectorAll(spots[i]).forEach(function (el) {
        if (el.querySelector('.cc-notice')) return;
        var n = document.createElement('p');
        n.className = 'cc-notice';
        n.style.cssText = 'font-size:1.1rem;opacity:.7;margin:0.6rem 0 0;line-height:1.3;';
        n.textContent = msg;
        el.appendChild(n);
      });
    }
  }

  // ---------- checkout hand-off ----------
  /* Checkout cannot run theme scripts, so the checkout UI extension (app
     "freeze-checkout-currency") reads two cart attributes instead and shows the
     same currency at the same rate the shopper saw here. Only POSTs when the
     values differ from what the cart already carries (CC_CONFIG.cartCurrency /
     cartRate, injected by the snippet), so this is normally a no-op. */
  var ATTR_CUR = C.attrCurrency || '_cc_currency';
  var ATTR_RATE = C.attrRate || '_cc_rate';
  function syncCartAttributes() {
    var wantCur = CUR === BASE ? '' : CUR;
    var wantRate = CUR === BASE ? '' : String(rate);
    var haveCur = C.cartCurrency || '';
    var haveRate = C.cartRate || '';
    if (wantCur === haveCur && wantRate === haveRate) return;
    if (!wantCur && !haveCur) return; // nothing to set and nothing to clear
    var attrs = {};
    attrs[ATTR_CUR] = wantCur;
    attrs[ATTR_RATE] = wantRate;
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).then(function () { log('cart attributes synced', attrs); }).catch(function () {});
  }

  // ---------- manual currency selector ----------
  // Built from the theme's own disclosure markup (see snippets/country-localization.liquid)
  // so it inherits the country picker's styling rather than rendering a raw
  // <select>, whose option list the OS draws and CSS cannot reach.
  /* Symbols come from Intl so every currency in the map is covered without a
     hand-written table going stale. */
  var symbolCache = {};
  function symbolOf(cur) {
    if (symbolCache[cur] != null) return symbolCache[cur];
    var sym = '';
    try {
      sym = new Intl.NumberFormat(undefined, { style: 'currency', currency: cur, minimumFractionDigits: 0, maximumFractionDigits: 0 })
        .formatToParts(0)
        .filter(function (part) { return part.type === 'currency'; })
        .map(function (part) { return part.value; })
        .join('');
    } catch (e) { sym = ''; }
    if (sym === cur) sym = ''; // no distinct symbol; the code alone reads better
    symbolCache[cur] = sym;
    return sym;
  }
  var CARET = '<svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"/></svg>';
  var CHECK = '<svg class="icon icon-checkmark" aria-hidden="true" focusable="false" viewBox="0 0 12 9" fill="none">' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M11.35.643a.5.5 0 01.006.707l-6.77 6.886a.5.5 0 01-.719-.006L.638 4.845a.5.5 0 11.724-.69l2.872 3.011 6.41-6.517a.5.5 0 01.707-.006h-.001z" fill="currentColor"/></svg>';

  function label(cur) { var sym = symbolOf(cur); return sym ? cur + ' ' + sym : cur; }

  function buildWidget(manual, idx, extraClass) {
    /* A short, deliberate list. The map covers ~30 currencies, but a 30-row
       dropdown is a worse control than five — the visitor's own currency is
       already chosen for them, and is appended here if it is not a regular. */
    var options = (C.selectorCurrencies || [BASE, 'USD', 'EUR', 'GBP', 'AED']).slice();
    if (CUR && options.indexOf(CUR) === -1) options.push(CUR);
    options = options.filter(function (v, i, a) { return v && a.indexOf(v) === i; });

    var active = manual ? manual.toUpperCase() : 'auto';
    var listId = 'cc-currency-list-' + idx;
    var wrap = document.createElement('div');
    wrap.className = 'disclosure cc-switcher' + (extraClass ? ' ' + extraClass : '');

    var items = [{ value: 'auto', text: 'Auto' }].concat(options.map(function (cu) {
      return { value: cu, text: label(cu) };
    }));

    wrap.innerHTML =
      '<button type="button" class="disclosure__button localization-form__select localization-selector link link--text caption-large"' +
      ' aria-expanded="false" aria-controls="' + listId + '" aria-label="Display currency">' +
      // Just the active currency: a longer "Auto | EUR €" label squeezes the
      // logo out of the mobile header. Whether Auto or a fixed currency is in
      // force is shown by the checkmark in the list below.
      '<span>' + label(CUR) + '</span>' + CARET +
      '</button>' +
      '<div class="disclosure__list-wrapper cc-switcher__panel" id="' + listId + '" hidden>' +
      '<ul role="list" class="list-unstyled disclosure__list">' +
      items.map(function (it) {
        var on = it.value === active;
        return '<li class="disclosure__item" tabindex="-1">' +
          '<a class="link link--text disclosure__link caption-large focus-inset" href="#" data-cc-value="' + it.value + '"' +
          (on ? ' aria-current="true"' : '') + '>' +
          '<span' + (on ? '' : ' class="visibility-hidden"') + '>' + CHECK + '</span>' +
          '<span class="country">' + it.text + '</span></a></li>';
      }).join('') +
      '</ul></div>';

    var button = wrap.querySelector('.disclosure__button');
    var panel = wrap.querySelector('.cc-switcher__panel');

    function close() { panel.hidden = true; button.setAttribute('aria-expanded', 'false'); }

    button.addEventListener('click', function (e) {
      e.preventDefault();
      var open = panel.hidden;
      panel.hidden = !open;
      button.setAttribute('aria-expanded', String(open));
    });
    wrap.addEventListener('click', function (e) {
      var link = e.target.closest ? e.target.closest('[data-cc-value]') : null;
      if (!link) return;
      e.preventDefault();
      try {
        if (link.dataset.ccValue === 'auto') localStorage.removeItem(LS_CUR);
        else localStorage.setItem(LS_CUR, link.dataset.ccValue);
      } catch (err) {}
      window.location.reload();
    });
    document.addEventListener('click', function (e) { if (!wrap.contains(e.target)) close(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });

    return wrap;
  }

  /* Mounted the way the theme mounts its own country picker: one copy in the
     desktop header (hidden on small screens by small-hide/medium-hide), one
     in the slim market row under the mobile header, and one inside the
     mobile menu drawer. Putting it in the mobile header row itself would
     shrink the logo from 154px to 101px and knock it off centre. */
  function buildSelector(manual) {
    if (!SHOW_SELECTOR) return;
    var hosts = [
      { el: document.querySelector('.desktop-localization-wrapper'), cls: 'small-hide medium-hide' },
      { el: document.querySelector('.header__mobile-localization'), cls: 'cc-switcher--bar' },
      { el: document.querySelector('.menu-drawer__localization'), cls: 'cc-switcher--drawer' }
    ];
    var mounted = 0;
    hosts.forEach(function (h, i) {
      if (!h.el || h.el.querySelector('.cc-switcher')) return;
      h.el.insertBefore(buildWidget(manual, i, h.cls), h.el.firstChild);
      mounted++;
    });
    if (mounted) return;
    // Themes without the standard localization areas: fall back to the icon row.
    var fallback = document.querySelector(C.selectorHost || '.header__icons');
    if (fallback && !fallback.querySelector('.cc-switcher')) {
      fallback.insertBefore(buildWidget(manual, 9, ''), fallback.firstChild);
    }
  }

  // ---------- init ----------
  function init() {
    var country = (C.country || '').toUpperCase();
    var manual = getManual();

    /* Picking a country in the theme's selector is a newer and more specific
       statement of where the shopper is than a currency they chose earlier, so
       a country change clears the override instead of being overruled by it.
       Staying in the same country leaves a chosen currency alone. */
    if (country) {
      var last = null;
      try { last = localStorage.getItem(LS_COUNTRY); } catch (e) {}
      if (manual && last && last !== country) {
        try { localStorage.removeItem(LS_CUR); } catch (e) {}
        manual = null;
        log('country changed', last, '->', country, '- cleared the currency override');
      }
      try { localStorage.setItem(LS_COUNTRY, country); } catch (e) {}
    }

    var pCur = manual
      ? Promise.resolve(manual.toUpperCase())
      : country
        ? Promise.resolve(mapCountry(country))
        : detectCountry().then(mapCountry);
    Promise.all([pCur, getRates()]).then(function (res) {
      CUR = (res[0] || BASE).toUpperCase();
      var rates = res[1] || {};
      if (CUR !== BASE && !rates[CUR]) { log('no rate for', CUR, '- falling back to', BASE); CUR = BASE; }
      rate = CUR === BASE ? 1 : (rates[CUR] || 1);
      log('display currency', CUR, 'rate', rate);
      if (CUR !== BASE) { convertAll(); startObserver(); injectNotice(); }
      syncCartAttributes();
      buildSelector(manual);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
